/**
 * PLPProductCard.jsx
 *
 * Product card for the Tool Finder Results page (Product Listing Page).
 * Supports both `grid` and `list` view modes. Matches Interactive Catalog
 * design (no Add to Cart — Sandvik is a B2B catalog, not a webshop).
 *
 * PORTED VERBATIM from TH ToolFinder v4 (Group D Sub-Plan 3, Task SP3.B.1):
 *   - Source: `tophammer-toolfinder-v4/src/components/product/PLPProductCard.jsx`
 *     (193 lines, full file).
 *
 * Behaviour contract (preserved from TF v4):
 *   - `viewMode === 'list'` renders a horizontal layout with image | content
 *     | compare column; `viewMode === 'grid'` (default) renders a stacked
 *     layout with image top, attributes mid, footer actions.
 *   - `isHovered` local state underlines the title on hover.
 *   - `getAttributeValue` resolves `product[attr.field]` and applies the
 *     attribute's optional `suffix`. Returns '-' for missing / 'None'.
 *   - Only the first 4 attributes are rendered.
 *   - `isRecommended` adds a 2px success-coloured border and a "REC" badge.
 *   - `isFavorite` toggles the bookmark fill colour.
 *   - `isComparing` toggles the compare checkbox.
 *   - Click handlers on title, image, "View details" → `onProductClick`.
 *   - Bookmark click → `onFavoriteToggle(itemCode)` (stops propagation).
 *   - Compare checkbox → `onCompareToggle(itemCode)`.
 *   - Contact link → `onContactClick(product)` (stops propagation).
 *
 * MRT adaptations (only):
 *   - Replaced `import { Icon } from '../ui'` with named lucide-react
 *     icons mapped from TF v4 names:
 *       precision_manufacturing → `Wrench` (closest analogue)
 *       bookmark → `Bookmark` (fill="currentColor" when favorited)
 *       mail → `Mail`
 *       chevron_right → `ChevronRight`
 *   - Sandvik no-rounded-corners rule: stripped every `rounded-lg` / `rounded`
 *     class. `rounded-full` is kept ONLY where it would belong on avatars /
 *     swatch dots (none in this component). `rounded-none` is left implicit
 *     by simply omitting the class.
 *   - Sandvik token names: `success` → `sandvik-success` (the token lives
 *     under `colors.sandvik.success` in tailwind.config.js, not at the
 *     top level — verified 2026-05-18).
 *
 * NEW MRT prop:
 *   - `availabilityBadge` (ReactNode, default null) — slot rendered below
 *     the title/itemCode header row in BOTH `grid` and `list` view modes,
 *     positioned just under the part number so a consumer can drop in
 *     `<RegionAvailabilityBadge itemCode={product.itemCode} />`. The slot
 *     renders nothing when the prop is falsy (no wrapper div), so cards
 *     for products that ARE sellable in the user's region show no gap.
 *     Wired by ResultsPage in SP3.B.2 — this file only exposes the slot.
 *
 * @module components/product/PLPProductCard
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.B.1)
 */
import { useState } from 'react';
import { Wrench, Bookmark, Mail, ChevronRight, ImageOff, ShoppingCart } from 'lucide-react';
import { isCommerceEnabledForProductLine, getProductDisplayName } from '@data/index';

export function PLPProductCard({
  product,
  attributes = [],
  viewMode = 'grid',
  onProductClick,
  onFavoriteToggle,
  onCompareToggle,
  onContactClick,
  onAddToCart,
  isFavorite = false,
  isComparing = false,
  isRecommended = false,
  availabilityBadge = null,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  const thumbSrc = product.imageUrl;
  const showImage = thumbSrc && !imgError;

  const getAttributeValue = (attr) => {
    const value = product[attr.field];
    if (!value || value === '-' || value === 'None') return '-';
    return `${value}${attr.suffix || ''}`;
  };

  // LIST VIEW - Desktop horizontal layout (matching catalog)
  if (viewMode === 'list') {
    return (
      <div className={`bg-white border overflow-hidden transition-shadow hover:shadow-md mb-3 ${
        isRecommended ? 'border-2 border-sandvik-success' : 'border-n2'
      }`}>
        <div className="flex items-stretch">
          {/* Image */}
          <div
            onClick={() => onProductClick?.(product)}
            className="w-28 lg:w-32 flex-shrink-0 bg-n1 flex items-center justify-center cursor-pointer border-r border-n2 overflow-hidden"
          >
            {showImage ? (
              <img
                src={thumbSrc}
                alt={product.description}
                className="w-full h-full object-contain p-1"
                loading="lazy"
                onError={() => setImgError(true)}
              />
            ) : (
              <ImageOff size={32} className="text-n3" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header Row */}
            <div
              className="px-4 py-3 border-b border-n2"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <h3
                    onClick={() => onProductClick?.(product)}
                    className={`font-normal text-sm text-electric-blue cursor-pointer line-clamp-2 ${isHovered ? 'underline' : ''}`}
                  >
                    {getProductDisplayName(product)}
                  </h3>
                  <p className="text-xs text-n5 mt-1">{product.itemCode}</p>
                  {availabilityBadge && (
                    <div className="mt-1">{availabilityBadge}</div>
                  )}
                </div>
                {isRecommended && (
                  <span className="px-2 py-0.5 bg-sandvik-success/10 text-sandvik-success text-xs font-normal flex-shrink-0">REC</span>
                )}
                {onFavoriteToggle && (
                  <button onClick={(e) => { e.stopPropagation(); onFavoriteToggle(product.itemCode); }} className="p-0.5 hover:bg-n1 flex-shrink-0">
                    <Bookmark
                      size={18}
                      fill={isFavorite ? 'currentColor' : 'none'}
                      className={isFavorite ? 'text-vibrant-orange' : 'text-n4'}
                    />
                  </button>
                )}
              </div>
            </div>

            {/* Attributes Row */}
            <div className="px-4 py-3 border-b border-n2 bg-white">
              <div className="grid grid-cols-4 gap-4">
                {attributes.slice(0, 4).map((attr, index) => (
                  <div key={attr.label || index} className="min-w-0">
                    <span className="text-xs text-n5 block truncate">{attr.label}</span>
                    <span className="text-sm font-medium text-carbon-black block truncate">{getAttributeValue(attr)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions Row */}
            <div className="px-4 py-2.5 flex items-center gap-4 bg-n1/30">
              <button onClick={() => onProductClick?.(product)} className="text-xs text-carbon-black flex items-center gap-1 hover:underline">
                View more details <ChevronRight size={16} className="text-vibrant-orange" />
              </button>
              {onAddToCart && isCommerceEnabledForProductLine(product.productLine) && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
                  className="text-xs text-white bg-electric-blue hover:bg-eb-3 px-3 py-1.5 transition-colors flex items-center gap-1 ml-auto"
                >
                  <ShoppingCart size={14} />
                  Add to cart
                </button>
              )}
              {onContactClick && (
                <button onClick={(e) => { e.stopPropagation(); onContactClick(product); }} className="text-xs text-electric-blue flex items-center gap-1 hover:underline">
                  <Mail size={14} /> Contact us
                </button>
              )}
            </div>
          </div>

          {/* Compare Column */}
          <div className="w-28 lg:w-32 flex-shrink-0 border-l border-n2 flex flex-col justify-center items-center px-3 bg-n1/30">
            {onCompareToggle && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={isComparing}
                  onChange={() => onCompareToggle(product.itemCode)}
                  className="w-4 h-4 rounded-none border-n3 accent-electric-blue"
                />
                Compare
              </label>
            )}
          </div>
        </div>
      </div>
    );
  }

  // GRID VIEW - Stacked layout with image top, label/value on separate lines
  return (
    <div className={`bg-white border overflow-hidden transition-shadow hover:shadow-md flex flex-col h-full ${
      isRecommended ? 'border-2 border-sandvik-success' : 'border-n2'
    }`}>
      {/* Header: Title + Bookmark */}
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3
              onClick={() => onProductClick?.(product)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={`font-normal text-sm text-electric-blue cursor-pointer line-clamp-2 leading-tight ${isHovered ? 'underline' : ''}`}
            >
              {getProductDisplayName(product)}
            </h3>
            <p className="text-xs text-n5 mt-1">{product.itemCode}</p>
            {availabilityBadge && (
              <div className="mt-1">{availabilityBadge}</div>
            )}
          </div>
          {onFavoriteToggle && (
            <button onClick={(e) => { e.stopPropagation(); onFavoriteToggle(product.itemCode); }} className="p-1 hover:bg-n1 flex-shrink-0">
              <Bookmark
                size={20}
                fill={isFavorite ? 'currentColor' : 'none'}
                className={isFavorite ? 'text-vibrant-orange' : 'text-n4'}
              />
            </button>
          )}
        </div>
      </div>

      {/* Image - Centered */}
      <div onClick={() => onProductClick?.(product)} className="px-4 cursor-pointer">
        <div className="bg-n1 flex items-center justify-center h-28 sm:h-32 overflow-hidden">
          {showImage ? (
            <img
              src={thumbSrc}
              alt={product.description}
              className="w-full h-full object-contain p-2"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <ImageOff size={40} className="text-n3" />
          )}
        </div>
      </div>

      {/* Attributes - Label and Value on separate lines */}
      <div onClick={() => onProductClick?.(product)} className="p-3 sm:p-4 cursor-pointer flex-1">
        <div className="space-y-2">
          {attributes.slice(0, 4).map((attr, index) => (
            <div key={attr.label || index} className={index > 0 ? 'pt-2 border-t border-n2' : ''}>
              <p className="text-xs text-n5">{attr.label}</p>
              <p className="text-sm font-medium text-carbon-black">{getAttributeValue(attr)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer: Actions */}
      <div className="px-3 sm:px-4 py-3 border-t border-n2 bg-n1/30">
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => onProductClick?.(product)} className="text-xs text-carbon-black flex items-center gap-0.5 hover:underline">
            View details <ChevronRight size={16} className="text-vibrant-orange" />
          </button>
          {onCompareToggle && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={isComparing}
                onChange={() => onCompareToggle(product.itemCode)}
                className="w-4 h-4 rounded-none accent-electric-blue"
              />
              Compare
            </label>
          )}
        </div>
        {onAddToCart && isCommerceEnabledForProductLine(product.productLine) && (
          <button
            onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
            className="w-full mt-2 py-2 bg-electric-blue hover:bg-eb-3 text-white text-xs font-sandvik-text font-normal transition-colors flex items-center justify-center gap-1.5"
          >
            <ShoppingCart size={14} />
            Add to cart
          </button>
        )}
        {onContactClick && (
          <button onClick={(e) => { e.stopPropagation(); onContactClick(product); }} className="mt-2 text-xs text-electric-blue flex items-center gap-1 hover:underline">
            <Mail size={14} /> Contact
          </button>
        )}
      </div>
    </div>
  );
}

export default PLPProductCard;
